import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { getImageUrl, prefetchCarImages } from '@/lib/image-utils';
import { supabase } from '@/integrations/supabase/client';

interface CarImage {
  storage_path: string;
  storage_path_avif?: string | null;
  storage_path_webp?: string | null;
  is_primary: boolean;
  position: number;
}

interface Car {
  id: string;
  brand: string;
  model: string;
  year: number;
  price: number;
  km: number;
  fuel: string;
  transmission: string;
  color: string;
  status: string;
  car_images?: CarImage[];
}

interface CarCardListProps {
  car: Car;
  className?: string;
}

export function CarCardList({ car, className }: CarCardListProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const prefetchCar = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: ['car', car.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('cars')
          .select('*, car_images(id, storage_path, storage_path_avif, storage_path_webp, is_primary, position)')
          .eq('id', car.id)
          .maybeSingle();
        if (error) throw error;
        // Prefetch gallery images into browser cache
        if (data?.car_images?.length) {
          prefetchCarImages(data.car_images);
        }
        return data;
      },
      staleTime: 30_000,
    });
  }, [car.id, queryClient]);
  const primaryImage = car.car_images?.find(img => img.is_primary) || car.car_images?.[0];

  // List view: smaller thumbnails (300px wide)
  const imgSize = { width: 300, height: 250 };
  const imageUrl = primaryImage
    ? getImageUrl(primaryImage.storage_path, imgSize)
    : null;
  const avifUrl = primaryImage?.storage_path_avif
    ? getImageUrl(primaryImage.storage_path_avif, imgSize)
    : null;
  const webpUrl = primaryImage?.storage_path_webp
    ? getImageUrl(primaryImage.storage_path_webp, imgSize)
    : null;

  const formattedPrice = new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(car.price);

  const formattedKm = new Intl.NumberFormat('pt-PT').format(car.km);
  const fuelLabel = t.fuel[car.fuel as keyof typeof t.fuel] || car.fuel;

  return (
    <Link
      to={`/carro/${car.id}`}
      onMouseEnter={prefetchCar}
      onTouchStart={prefetchCar}
      className={cn(
        'flex items-center gap-3 p-2 rounded-xl bg-card/60 backdrop-blur-sm border border-border/30 transition-all duration-300 hover:bg-card/80',
        className
      )}
    >
      {/* Image in liquid glass card */}
      <div className="w-24 h-20 shrink-0 rounded-lg overflow-hidden bg-background/40 backdrop-blur-md border border-white/10 shadow-sm">
        {imageUrl ? (
          <picture>
            {avifUrl && <source srcSet={avifUrl} type="image/avif" />}
            {webpUrl && <source srcSet={webpUrl} type="image/webp" />}
            <img
              src={imageUrl}
              alt={`${car.brand} ${car.model}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </picture>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-muted-foreground text-[10px]">Sem imagem</span>
          </div>
        )}
      </div>

      {/* Content aligned right */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5">
        <h3 className="font-semibold text-sm truncate">
          {car.brand} {car.model}
        </h3>
        <div className="flex flex-wrap gap-1">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-md">
            {car.year}
          </Badge>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-md">
            {formattedKm} km
          </Badge>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-md">
            {fuelLabel}
          </Badge>
        </div>
      </div>

      {/* Price on the far right */}
      <div className="shrink-0 pr-1">
        <Badge className="bg-primary text-primary-foreground text-xs px-2 py-0.5 font-bold rounded-md">
          {formattedPrice}
        </Badge>
      </div>
    </Link>
  );
}
